<template>
  <div class="stock-quote" v-if="stock && stock.length !== 0">
    <div class="stock-quote__highlight">
      <div class="stock-quote__symbol">{{ stock.market }} : {{ stock.symbol }}</div>
      <div class="stock-quote__lastdone">{{ stock.last_done }} <span class="stock-quote__currency">{{ stock.currency }}</span></div>
      <div class="stock-quote__updated">{{ translate("last.updated") }} : <span class="text-uppercase">{{ stock.datetime }}</span></div>
    </div>
    <div class="stock-quote__body">
        <div class="stock-quote__row">
          <div class="stock-quote__block">
            <div class="stock-quote__title">{{ translate("change") }} ({{ translate("percent.change") }})</div>
            <div class="stock-quote__value">{{ stock.change }} ({{ stock.percent_change }}%)</div>
          </div>
          <div class="stock-quote__block">
            <div class="stock-quote__title">{{ translate("days.range") }}</div>
            <div class="stock-quote__value">{{ stock.low }} - {{ stock.high }}</div>
          </div>
          <div class="stock-quote__block">
            <div class="stock-quote__title">{{ translate("52weeks.range") }}</div>
            <div class="stock-quote__value">{{ stock.low_52weeks }} - {{ stock.high_52weeks }}</div>
          </div>
        </div>
        <div class="stock-quote__row">
          <div class="stock-quote__block">
            <div class="stock-quote__title">{{ translate("volume.shares") }}</div>
            <div class="stock-quote__value">{{ stock.volume }}</div>
          </div>
          <div class="stock-quote__block">
            <div class="stock-quote__title">{{ translate("value.baht") }}</div>
            <div class="stock-quote__value">{{ stock.value }}</div>
          </div>
          <div class="stock-quote__block stock-quote__block--column">
               <div class="stock-quote__item">
                  <div class="stock-quote__title">{{ translate("price.open") }}</div>
                  <div class="stock-quote__value">{{ stock.open }}</div>
               </div>
               <div class="stock-quote__item">
                   <div class="stock-quote__title">{{ translate("prior") }}</div>
                  <div class="stock-quote__value">{{ stock.prior }}</div>
              </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  stock: {
    type: [Object, Array], // Support both Object and Array
    required: true,
  },
});

import { useTranslationStore } from "@/stores/translations"; // Import the store

// ดึง store สำหรับการแปล
const translationStore = useTranslationStore();

// สร้างฟังก์ชันสำหรับการแปล
const translate = translationStore.translate;
</script>

<style lang="scss" scoped>
@import "@/assets/scss/app";

@each $lang in $languages {
  html[lang="#{$lang}"] {
    .stock-quote {
      .stock-quote__symbol {
        @include font-family('heading', $lang);
        @include font-size('h1', $lang);
        @include font-weight('bold', $lang);
      }
      .stock-quote__lastdone {
        @include font-family('heading', $lang);
        @include font-size('display-1', $lang);
        @include font-weight('bold', $lang);
        line-height: 1;
      }
      .stock-quote__currency {
        @include font-size('h1', $lang);
      }
      .stock-quote__title {
        @include font-weight('medium', $lang);
      }
      .stock-quote__value {
        @include font-family('heading', $lang);
        @include font-size('display-2', $lang);
        @include font-weight('bold', $lang);
        @include media-breakpoint-down(lg) {
            @include font-size('h1', $lang);
        }
        @include media-breakpoint-down(sm) {
            @include font-size('h3', $lang);
        }
      }
    }
  }
}

.stock-quote {
     display: flex;
     align-items: center;
     @include media-breakpoint-down(md) {
        flex-direction: column;
    }
      .stock-quote__highlight {
        border-radius: 24px;
        border: 1px solid rgba($white, 0.50);
        min-width: 267px;
        min-height: 225px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 1.5rem 0.5rem 1.5rem 2rem;
        @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
          -webkit-backdrop-filter: blur(4px);
          backdrop-filter: blur(4px);
          @include primary-gradient(rgba($white, 0.40) 0%, rgba($darkgreen-1, 0.40) 123.43%, 180deg);
        }
        @include media-breakpoint-down(md) {
          width: 100%;
        }
      }
      .stock-quote__lastdone {
        margin: 0.5rem 0;
      }
      .stock-quote__body {
        flex-grow: 1;
        padding: 0 0 0 3rem;
        @include media-breakpoint-down(lg) {
          padding: 0 0 0 1.5rem;
        }
        @include media-breakpoint-down(md) {
          width: 100%;
          padding: 0;
          margin: 1.5rem 0 0 0;
        }
      }
      .stock-quote__row {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        &:not(:first-child) {
            border-top: 1px solid rgba($white, 0.2);
            .stock-quote__block {
              padding: 1.5rem 0 1rem 2rem;
              &.stock-quote__block--column {
                display: flex;
                align-items: center;
                padding: 0;
              }
              @include media-breakpoint-down(sm) {
                padding: 0.35rem 2rem;
                &.stock-quote__block--column {
                  flex-direction: column;
                }
              }
            }
        }
      }
      .stock-quote__block {
        width: 33.33%;
        padding: 0 0 1.5rem 2rem;
        &:not(:first-child) {
            border-left: 1px solid rgba($white, 0.2);
        }
        @include media-breakpoint-down(sm) {
          width: 100%;
          padding: 0.35rem 2rem;
          &:not(:first-child) {
            border-left: none;
            border-top: 1px solid rgba($white, 0.2);
          }
        }
      }
      .stock-quote__symbol {
        text-transform: uppercase;
        color: $green-2;
      }
      .stock-quote__title {
        color: $green-2;
      }
      .stock-quote__value {
        margin: 0.5rem 0 0 0;
        @include media-breakpoint-down(sm) {
          margin: 0.25rem 0 0 0;
        }
      }
      .stock-quote__item {
        height: 100%;
        width: 50%;
        padding: 1.5rem 1.5rem 1rem 2rem;
        &:not(:first-child) {
            border-left: 1px solid rgba($white, 0.2);
            padding: 1.5rem 0 1rem 2rem;
        }
        @include media-breakpoint-down(sm) {
          width: 100%;
          height: auto;
          padding: 0.35rem 2rem;
          &:not(:first-child) {
            border-left: none;
            border-top: 1px solid rgba($white, 0.2);
            padding: 0.35rem 2rem;
          }
        }
      }
}

</style>
